import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import styled, {css} from 'styled-components'
import {ArrowDown, ArrowUp, Columns3Cog, GripVertical} from 'lucide-react'
import {Checkbox, Tooltip} from '../../BaseComponents'
import {Badge, Button, VisuallyHidden} from '../../Primitives'
import {Popover, type PopoverCloseReason} from '../Popover'

/**
 * ColumnSettings — a table's Columns control: a trigger button and a panel listing every column
 * in table order, to show, hide and reorder them. Dumb: the caller owns the layout (the pure model
 * in `./columnLayout`, through a connected hook) and passes rows and callbacks; changes apply as
 * they are made. Spec: the batch-table-columns tracker, §5.4–§5.6.
 *
 * - Locked rows (pinned columns) are checked and disabled, read "Always shown", and have no drag
 *   handle or move buttons.
 * - The last visible optional column can't be unchecked and says why.
 * - Reorder by dragging a row's handle (pointer), or with its Move up / Move down buttons (the
 *   keyboard and touch way). `onMove(id, toIndex)` counts optional columns only, like
 *   `moveColumn`. After a move the same row keeps focus on the same button, or on the other one
 *   once that end is reached, and its row is scrolled into the list's view.
 * - A polite live region reads each move, hide, show and reset.
 * - Focus: opening focuses the first enabled checkbox; a `notice` is the dialog's accessible
 *   description, so it is read on open even though focus lands on the first checkbox after it.
 *   After a Reset the first enabled checkbox's row is scrolled into view and takes focus. Escape
 *   returns focus to the trigger. An outside press returns it only when the press did not land on
 *   a control (decided from the press target, not from where focus went), without scrolling to it.
 *   The panel is portaled to the end of `<body>`, so Tab past its last control or Shift+Tab before
 *   its first closes it and focuses the trigger, and focus landing anywhere else closes it and
 *   stays there.
 */

export interface ColumnSettingsItem {
  id: string
  label: string
  visible: boolean
  /** A pinned column: listed, always shown, never moved. */
  locked?: boolean
  /** False for the last visible optional column (the model's `canHideColumn`). */
  canHide: boolean
}

export type ColumnSettingsSize = 'sm' | 'md'

export interface ColumnSettingsProps {
  /** Every listed column, in current table order. */
  items: ColumnSettingsItem[]
  onToggle: (id: string) => void
  /** `toIndex` counts optional (unlocked) columns only, hidden ones included. */
  onMove: (id: string, toIndex: number) => void
  onReset: () => void
  /** Visibility and order both match the default: no badge, Reset disabled. */
  isDefault: boolean
  visibleCount: number
  /** True until the saved layout has loaded or failed to. */
  disabled?: boolean
  /** An icon-only trigger, named by `aria-label` and labelled by a Tooltip. */
  compact?: boolean
  size?: ColumnSettingsSize
  /** The trigger's label and the panel's heading. Default 'Columns'. */
  label?: string
  /** Shown above the list, e.g. that saved columns couldn’t load. */
  notice?: ReactNode
  'data-testid': string
}

type MoveDirection = 'up' | 'down'
type PendingFocus = {kind: 'move'; id: string; direction: MoveDirection} | {kind: 'reset'}

const MINIMUM_HINT = 'At least one column must stay visible'
const ALWAYS_SHOWN = 'Always shown'
/** A press inside one of these lands on a control, which keeps focus or, in Safari, drops it. */
const FOCUSABLE_PRESS =
  'a[href], area[href], button, input, select, textarea, label, summary, iframe, [tabindex], [contenteditable]:not([contenteditable="false"])'

const iconBox = css`
  flex: none;
  width: var(--space-4);
  height: var(--space-4);
`

const TriggerButton = styled(Button)<{$compact: boolean}>`
  ${({$compact}) =>
    $compact &&
    css`
      padding-inline: var(--space-3);
    `}
`

const TriggerIcon = styled(Columns3Cog)`
  ${iconBox}
`

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 16rem;
  /* The surface's own height cap, less its border, so the footer never scrolls away. */
  max-height: calc(var(--popover-max-height) - 2 * var(--border-thin));
  font-family: var(--font-sans);
`

const Heading = styled.h2`
  margin: 0;
  padding: var(--space-4) var(--space-4) var(--space-2);
  font-size: var(--fs-subtitle);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-tight);
  color: var(--text-primary);
`

const Notice = styled.div`
  margin: 0 var(--space-4) var(--space-2);
  font-size: var(--fs-caption);
  line-height: var(--lh-normal);
  color: var(--text-secondary);
`

const List = styled.ul`
  flex: 1 1 auto;
  min-height: 0;
  max-height: 22rem;
  margin: 0;
  padding: 0 var(--space-2) var(--space-2);
  overflow-y: auto;
  list-style: none;
`

const Row = styled.li<{$dragging: boolean}>`
  position: relative;
  padding: var(--space-1) var(--space-2);
  opacity: ${({$dragging}) => ($dragging ? 0.5 : 1)};

  /* No hover tint: the row is not a control, and the --text-secondary captions fall under 4.5:1
     on --bg-hover. */

  /* The drop indicator: a line on the edge the dragged column would land on. */
  &[data-drop-edge]::before {
    content: '';
    position: absolute;
    right: var(--space-2);
    left: var(--space-2);
    height: var(--border-thick);
    border-radius: var(--radius-pill);
    background: var(--brand-cta);
  }
  &[data-drop-edge='before']::before {
    top: calc(var(--border-thick) / -2);
  }
  &[data-drop-edge='after']::before {
    bottom: calc(var(--border-thick) / -2);
  }
`

const RowMain = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  min-height: var(--btn-height-sm);
`

const Handle = styled.span`
  display: inline-flex;
  flex: none;
  color: var(--text-secondary);
  cursor: grab;

  &:active {
    cursor: grabbing;
  }

  svg {
    ${iconBox}
  }
`

/* Holds a locked row's checkbox in line with the rows that have a handle. */
const HandleSlot = styled.span`
  ${iconBox}
`

const CheckboxSlot = styled.div`
  flex: 1;
  min-width: 0;
`

const AlwaysShown = styled.span`
  flex: none;
  font-size: var(--fs-caption);
  color: var(--text-secondary);
`

const MoveButtons = styled.div`
  display: flex;
  flex: none;
  gap: var(--space-1);
`

const MoveButton = styled(Button)`
  padding: 0;
  aspect-ratio: 1;

  svg {
    ${iconBox}
  }
`

/* Lines up under the label: the handle slot, the gap, the DS checkbox box (1.125rem) and its gap. */
const MinimumHint = styled.p`
  margin: 0;
  padding-inline-start: calc(var(--space-4) + var(--space-2) + 1.125rem + var(--space-2));
  font-size: var(--fs-caption);
  line-height: var(--lh-normal);
  color: var(--text-secondary);
`

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: var(--space-2) var(--space-4);
  border-top: var(--border-thin) solid var(--border-subtle);
`

/**
 * Focuses the first enabled checkbox. On open the list is freshly mounted at the top and the
 * surface may still sit at its provisional spot, so nothing may scroll. After a Reset the list is
 * usually scrolled to its end (the keyboard reaches Reset by tabbing through every row), so
 * `reveal` first scrolls that checkbox's row back into the list's view. Scroll the row, not the
 * 1px visually-hidden input, so the whole row and the checkbox's focus ring show.
 */
const focusFirstCheckbox = (panel: HTMLElement | null, {reveal = false} = {}) => {
  const checkbox = panel?.querySelector<HTMLInputElement>('input[type="checkbox"]:not(:disabled)')
  if (!checkbox) return
  // Optional call: jsdom has no scrollIntoView (same guard as the DS Select).
  if (reveal) checkbox.closest('li')?.scrollIntoView?.({block: 'nearest'})
  checkbox.focus({preventScroll: true})
}

/** What Tab reaches inside the panel, in order (the DS Modal's focus-trap rule). */
const tabbables = (panel: HTMLElement, active: Element | null) =>
  Array.from(
    panel.querySelectorAll<HTMLElement>(
      'button, [href], input:not([type="hidden"]), select, textarea, [tabindex]',
    ),
  ).filter((el) => el === active || (el.tabIndex >= 0 && !el.matches(':disabled')))

export function ColumnSettings({
  items,
  onToggle,
  onMove,
  onReset,
  isDefault,
  visibleCount,
  disabled = false,
  compact = false,
  size = 'md',
  label = 'Columns',
  notice,
  'data-testid': testId,
}: ColumnSettingsProps) {
  const uid = useId()
  const panelId = `${uid}-panel`
  const headingId = `${uid}-heading`
  const noticeId = `${uid}-notice`
  const [open, setOpen] = useState(false)
  const [trigger, setTrigger] = useState<HTMLButtonElement | null>(null)
  const [announcement, setAnnouncement] = useState('')
  const [drag, setDrag] = useState<{id: string; gap: number | null} | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const resetRef = useRef<HTMLButtonElement>(null)
  const moveButtons = useRef(new Map<string, HTMLButtonElement>())
  const pendingFocus = useRef<PendingFocus | null>(null)
  const restoreTimer = useRef<number>()

  const isOpen = open && !disabled
  const optional = items.filter((item) => !item.locked)
  const optionalIndex = (id: string) => optional.findIndex((item) => item.id === id)

  // A trigger disabled while open closes its panel for good, not until it is enabled again.
  useEffect(() => {
    if (disabled) setOpen(false)
  }, [disabled])

  useEffect(() => () => window.clearTimeout(restoreTimer.current), [])

  // After a move, focus stays on the button pressed: rows are keyed by id, and React gives focus
  // back to a node it moves. Once the row reaches an end that button is disabled, so focus goes to
  // the other one; either way its row is then scrolled into the list's view. After a Reset, which
  // disables itself, focus goes to the first checkbox, its row scrolled into view.
  useLayoutEffect(() => {
    const pending = pendingFocus.current
    if (!pending) return
    pendingFocus.current = null
    if (pending.kind === 'reset') {
      if (resetRef.current?.disabled) focusFirstCheckbox(panelRef.current, {reveal: true})
      return
    }
    const other: MoveDirection = pending.direction === 'up' ? 'down' : 'up'
    const target = [pending.direction, other]
      .map((direction) => moveButtons.current.get(`${pending.id}:${direction}`))
      .find((button) => button && !button.disabled)
    if (!target) return
    if (document.activeElement !== target) target.focus({preventScroll: true})
    // React DOM refocuses a row it moves but writes every ancestor's scroll position back, and a
    // row that stays put just shifts, so the focused button can end up outside the list's view.
    // Scroll its whole row (whose padding holds the focus ring) to the nearest edge. jsdom has no
    // scrollIntoView.
    target.closest('li')?.scrollIntoView?.({block: 'nearest'})
  })

  // Focus lands when the panel actually mounts, the first open included.
  const panelCallbackRef = useCallback((panel: HTMLDivElement | null) => {
    panelRef.current = panel
    focusFirstCheckbox(panel)
  }, [])

  const handleClose = useCallback(
    (reason: PopoverCloseReason, event: KeyboardEvent | MouseEvent | FocusEvent) => {
      setOpen(false)
      setDrag(null)
      window.clearTimeout(restoreTimer.current)
      // Focus already went where the user sent it: never pull it back.
      if (reason === 'focusOut') return
      if (reason === 'escapeKeyDown') {
        trigger?.focus() // keyboard: scrolling to the focused trigger is wanted here
        return
      }
      // A press on another control leaves focus to the browser: Chrome focuses it, Safari and
      // macOS Firefox focus nothing. Decided from the press, since focus alone can't tell them apart.
      const {target} = event
      if (target instanceof Element && target.closest(FOCUSABLE_PRESS)) return
      // Any other press: once its handlers have run, give focus back if nothing took it. Without
      // scrolling: the panel stays on screen while its trigger scrolls away.
      restoreTimer.current = window.setTimeout(() => {
        const active = document.activeElement
        if (!active || active === document.body) trigger?.focus({preventScroll: true})
      }, 0)
    },
    [trigger],
  )

  // The panel is portaled to the end of <body>, so Tab order does not run from the trigger into it
  // (WCAG F85): its edges close it and hand focus back to the trigger.
  const handlePanelKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab' || event.defaultPrevented) return
    const active = document.activeElement
    const reachable = tabbables(event.currentTarget, active)
    const edge = event.shiftKey ? reachable[0] : reachable[reachable.length - 1]
    if (!edge || active !== edge) return
    event.preventDefault()
    setOpen(false)
    setDrag(null)
    trigger?.focus()
  }

  const moveTo = (item: ColumnSettingsItem, toIndex: number) => {
    onMove(item.id, toIndex)
    setAnnouncement(`${item.label} moved to position ${toIndex + 1} of ${optional.length}`)
  }

  const handleMoveClick = (item: ColumnSettingsItem, direction: MoveDirection) => {
    const from = optionalIndex(item.id)
    pendingFocus.current = {kind: 'move', id: item.id, direction}
    moveTo(item, direction === 'up' ? from - 1 : from + 1)
  }

  const handleToggle = (item: ColumnSettingsItem) => {
    onToggle(item.id)
    setAnnouncement(`${item.label} ${item.visible ? 'hidden' : 'shown'}`)
  }

  const handleReset = () => {
    onReset()
    pendingFocus.current = {kind: 'reset'}
    setAnnouncement(`${label} reset to default`)
  }

  const moveButtonRef =
    (id: string, direction: MoveDirection) => (button: HTMLButtonElement | null) => {
      const key = `${id}:${direction}`
      if (button) moveButtons.current.set(key, button)
      else moveButtons.current.delete(key)
    }

  /** The gap a drop over `item` lands in, 0…optional.length: before or after the row's middle. */
  const dropGap = (event: React.DragEvent<HTMLLIElement>, item: ColumnSettingsItem) => {
    const before = items.slice(0, items.indexOf(item)).filter((other) => !other.locked).length
    if (item.locked) return before
    const rect = event.currentTarget.getBoundingClientRect()
    return event.clientY > rect.top + rect.height / 2 ? before + 1 : before
  }

  const handleDragStart = (event: React.DragEvent<HTMLSpanElement>, item: ColumnSettingsItem) => {
    const transfer = event.dataTransfer as DataTransfer | undefined
    if (transfer) {
      transfer.effectAllowed = 'move'
      transfer.setData('text/plain', item.id) // Firefox starts a drag only with data
      const row = event.currentTarget.closest('li')
      if (row) {
        const rect = row.getBoundingClientRect()
        transfer.setDragImage(row, event.clientX - rect.left, event.clientY - rect.top)
      }
    }
    setDrag({id: item.id, gap: null})
  }

  const handleDragOver = (event: React.DragEvent<HTMLLIElement>, item: ColumnSettingsItem) => {
    if (!drag) return // not a drag that started on one of these handles
    event.preventDefault()
    const transfer = event.dataTransfer as DataTransfer | undefined
    if (transfer) transfer.dropEffect = 'move'
    const gap = dropGap(event, item)
    if (gap !== drag.gap) setDrag({id: drag.id, gap})
  }

  const handleDrop = (event: React.DragEvent<HTMLLIElement>, item: ColumnSettingsItem) => {
    if (!drag) return
    event.preventDefault()
    const from = optionalIndex(drag.id)
    const dragged = optional[from]
    const gap = dropGap(event, item)
    setDrag(null)
    if (!dragged) return
    const toIndex = from < gap ? gap - 1 : gap
    if (toIndex !== from) moveTo(dragged, toIndex)
  }

  const handleDragEnd = () => setDrag(null)

  // Where the drop line shows: nowhere for a drop that would leave the column where it is.
  const dragFrom = drag ? optionalIndex(drag.id) : -1
  const indicator = (() => {
    const gap = drag?.gap
    if (gap === null || gap === undefined || gap === dragFrom || gap === dragFrom + 1) return null
    const next = optional[gap]
    if (next) return {id: next.id, edge: 'before' as const}
    const last = optional[optional.length - 1]
    return last ? {id: last.id, edge: 'after' as const} : null
  })()

  const triggerName = isDefault ? label : `${label}, ${visibleCount} shown`
  const triggerButton = (
    <TriggerButton
      ref={setTrigger}
      type="button"
      variant="secondary"
      size={size}
      $compact={compact}
      disabled={disabled}
      aria-haspopup="dialog"
      aria-expanded={isOpen}
      aria-controls={isOpen ? panelId : undefined}
      aria-label={compact || !isDefault ? triggerName : undefined}
      iconLeft={<TriggerIcon aria-hidden="true" />}
      onClick={() => setOpen((current) => !current)}
      data-testid={testId}>
      {compact ? null : label}
      {isDefault ? null : (
        <Badge variant="brand" solid dot={false} aria-hidden="true" data-testid={`${testId}-count`}>
          {visibleCount}
        </Badge>
      )}
    </TriggerButton>
  )

  return (
    <>
      {compact ? (
        <Tooltip content={label} describeChild={false}>
          {triggerButton}
        </Tooltip>
      ) : (
        triggerButton
      )}
      <Popover
        open={isOpen}
        anchorEl={trigger}
        onClose={handleClose}
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
        transformOrigin={{vertical: 'top', horizontal: 'right'}}
        id={panelId}
        aria-labelledby={headingId}
        aria-describedby={notice ? noticeId : undefined}
        disableAutoFocus
        data-testid={`${testId}-panel`}>
        <Panel ref={panelCallbackRef} onKeyDown={handlePanelKeyDown}>
          <Heading id={headingId}>{label}</Heading>
          {notice ? <Notice id={noticeId}>{notice}</Notice> : null}
          <List>
            {items.map((item) => {
              const position = optionalIndex(item.id)
              const minimum = !item.locked && item.visible && !item.canHide
              const noteId = `${uid}-note-${item.id}`
              return (
                <Row
                  key={item.id}
                  data-testid={`${testId}-item-${item.id}`}
                  data-drop-edge={indicator?.id === item.id ? indicator.edge : undefined}
                  $dragging={drag?.id === item.id}
                  onDragOver={(event) => handleDragOver(event, item)}
                  onDrop={(event) => handleDrop(event, item)}>
                  <RowMain>
                    {item.locked ? (
                      <HandleSlot aria-hidden="true" />
                    ) : (
                      <Handle
                        aria-hidden="true"
                        draggable
                        onDragStart={(event) => handleDragStart(event, item)}
                        onDragEnd={handleDragEnd}>
                        <GripVertical />
                      </Handle>
                    )}
                    <CheckboxSlot>
                      <Checkbox
                        label={item.label}
                        checked={Boolean(item.locked) || item.visible}
                        disabled={Boolean(item.locked) || minimum}
                        aria-describedby={item.locked || minimum ? noteId : undefined}
                        onChange={() => handleToggle(item)}
                        data-testid={`${testId}-checkbox-${item.id}`}
                      />
                    </CheckboxSlot>
                    {item.locked ? (
                      <AlwaysShown id={noteId}>{ALWAYS_SHOWN}</AlwaysShown>
                    ) : (
                      <MoveButtons>
                        <MoveButton
                          ref={moveButtonRef(item.id, 'up')}
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-label={`Move ${item.label} up`}
                          disabled={position <= 0}
                          onClick={() => handleMoveClick(item, 'up')}
                          data-testid={`${testId}-move-up-${item.id}`}>
                          <ArrowUp aria-hidden="true" />
                        </MoveButton>
                        <MoveButton
                          ref={moveButtonRef(item.id, 'down')}
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-label={`Move ${item.label} down`}
                          disabled={position >= optional.length - 1}
                          onClick={() => handleMoveClick(item, 'down')}
                          data-testid={`${testId}-move-down-${item.id}`}>
                          <ArrowDown aria-hidden="true" />
                        </MoveButton>
                      </MoveButtons>
                    )}
                  </RowMain>
                  {minimum ? <MinimumHint id={noteId}>{MINIMUM_HINT}</MinimumHint> : null}
                </Row>
              )
            })}
          </List>
          <Footer>
            <Button
              ref={resetRef}
              type="button"
              variant="tertiary"
              size="sm"
              disabled={isDefault}
              onClick={handleReset}
              data-testid={`${testId}-reset`}>
              Reset to default
            </Button>
          </Footer>
        </Panel>
      </Popover>
      <VisuallyHidden role="status" aria-live="polite" aria-atomic="true">
        {announcement}
      </VisuallyHidden>
    </>
  )
}
